// @flow
import { commitMutation, graphql } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import CartPromoCodeAutoAddMutation from './CartPromoCodeAutoAddMutation';
import CartDeleteMutation from './CartDeleteMutation';

import { trackEvent } from '../../utils/tracking';
import { purchaseWithPurchaseProductIds } from '../../components/_common/Cart/pwpConfig';

export const mutation = graphql`
  mutation CartAddMutation($input: CartAddInput!) {
    cartAdd(input: $input) {
      cartEntryEdge {
        node {
          id
          quantity
          product {
            id
            productId
          }
        }
      }
      cart {
        id
        autoShipEnabled
        autoShipIntervalUnit
        autoShipIntervalCount
        c4cMode
        enrollingAsPc
        totalSavings
        promoCodeSavings
        pcPotentialSavings
        viaQualifiedCartTotal
        totalNumberOfItems
        subTotalPriceOfItems
        subTotalPriceOfItemsWithPromoCodes
        eventId
        event {
          id
          trinityPartyType
        }
        promoCodes {
          code
          promoDescription
          thresholds {
            discountAmount
            discountAmountEach
          }
        }
        entries(first: 1000) {
          edges {
            cursor
            node {
              id
              quantity
              product {
                id
                price
                name
                priceSale
                productId
                wineBottleQuantity
                isWinePack
                productPermissions {
                  oneCommerce {
                    preferredCustomer {
                      add
                    }
                  }
                }
                images {
                  squareFront
                  productCatalog
                  productFront
                  productBack
                }
                promoCodeDiscount {
                  case4Cause {
                    case6SKU
                    case12SKU
                    donationEligible
                    case12PriceUSD
                    case6PriceUSD
                  }
                }
              }
            }
          }
        }
        entriesV2(first: 1000) {
          edges {
            cursor
            node {
              id
              quantity
              product {
                name
                id
                productId
                price
                finalPrice
                productPermissions {
                  oneCommerce {
                    preferredCustomer {
                      add
                    }
                  }
                }
                images {
                  squareFront
                  productCatalog
                  productFront
                  productBack
                }
              }
            }
          }
        }
        promoCodeDiscountedItems {
          id
          nonDiscountedSkus {
            edges {
              cursor
              node {
                id
                sku
                discountAmount
                productId
                quantity
                product {
                  id
                  productId
                  productURLslug
                  name
                  price
                  priceSale
                  wineBottleQuantity
                  isWinePack
                  productPermissions {
                    oneCommerce {
                      preferredCustomer {
                        add
                      }
                    }
                  }
                  promoCodeDiscount {
                    case4Cause {
                      case6PriceUSD
                    }
                  }
                  images {
                    productCatalog
                    productFront
                    productBack
                    squareFront
                  }
                }
              }
            }
          }
          discountedSkus {
            edges {
              cursor
              node {
                id
                sku
                discountAmount
                productId
                quantity
                product {
                  id
                  productId
                  productURLslug
                  name
                  price
                  priceSale
                  wineBottleQuantity
                  isWinePack
                  productPermissions {
                    oneCommerce {
                      preferredCustomer {
                        add
                      }
                    }
                  }
                  promoCodeDiscount {
                    case4Cause {
                      case6PriceUSD
                    }
                  }
                  images {
                    productCatalog
                    productFront
                    productBack
                    squareFront
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export function sharedUpdater(store, cart, newEdge) {
  const cartProxy = store.get(cart.id);
  const entries = ConnectionHandler.getConnection(cartProxy, 'Cart_entries');
  ConnectionHandler.insertEdgeBefore(entries, newEdge);
}

export function getVariables(cart, product, quantity) {
  const updatedCartItems = cart.entriesV2
    ? cart.entriesV2.edges.map(entry => entry.node.product.productId)
    : cart.entries.edges.map(entry => entry.node.product.productId);
  const { id, productId } = product;
  return {
    id,
    quantity,
    cartItems: updatedCartItems,
    productApiId: productId,
  };
}

const getCartItem = ({ cart, pwpProductId }) => {
  return cart.entries.edges.find(
    ({ node: cartEntry }) => cartEntry.product.productId === pwpProductId,
  );
};

function onCompletedDefault(defaultParams) {
  const { isCheckout, router, environment, response, user } = defaultParams;
  const { totalNumberOfItems, enrollingAsPc } = response.cartAdd.cart;
  if (!totalNumberOfItems) {
    if (isCheckout) {
      router.push('/shop');
    }
  }
  const { viaQualifiedCartTotal, entries } = response.cartAdd.cart;
  const purchaseWithPurchase = entries.edges.filter(updatedCartEntry => {
    const updatedCartEntryProductId =
      updatedCartEntry.node.product && updatedCartEntry.node.product.productId;
    return purchaseWithPurchaseProductIds.includes(updatedCartEntryProductId);
  });
  const purchaseWithPurchaseInvalid = viaQualifiedCartTotal < 6;

  if (purchaseWithPurchase.length && purchaseWithPurchaseInvalid) {
    const pwpProductId = purchaseWithPurchase[0].node.product.productId;
    const cartItem = getCartItem({ cart: response.cartAdd.cart, pwpProductId });
    CartDeleteMutation.commit(
      environment,
      response.cartAdd.cart,
      cartItem.node,
      user,
      false,
    );
  }
  // if (!enrollingAsPc) {
  //   return CartPromoCodeAutoAddMutation.commit(environment);
  // }
}

export function getOnCompleted(onCompleted, defaultParams) {
  const { product, response, user, qtyChange } = defaultParams;
  if (onCompleted) {
    onCompleted();
  }
  onCompletedDefault(defaultParams);
  if (qtyChange > 0) {
    const {
      location: { pathname },
    } = window;
    let path;

    switch (true) {
      case pathname.includes('shop/product'): {
        path = 'Product Detail';
        break;
      }
      case pathname.includes('checkout'): {
        path = 'Checkout';
        break;
      }
      case pathname.includes('favorites'): {
        path = 'Favorites';
        break;
      }
      case pathname.includes('shop'):
      default:
        path = 'Catalog';
    }

    trackEvent({
      user,
      event: 'Product Added',
      properties: {
        user,
        cart: { qtyChange, ...response.cartAdd.cart },
        product,
        label: path,
      },
    });
  } else {
    trackEvent({
      user,
      event: 'Product Removed',
      properties: {
        product,
        cart: { qtyChange, ...response.cartAdd.cart },
        label: 'remove',
      },
    });
  }
}

// const userIsPC = user =>
//   user && user.conciergeUser
//     ? user.conciergeUser.permissions &&
//       user.conciergeUser.permissions.oneCommerce &&
//       user.conciergeUser.permissions.oneCommerce.preferredCustomer &&
//       user.conciergeUser.permissions.oneCommerce.preferredCustomer.access
//     : user &&
//       user.permissions &&
//       user.permissions.oneCommerce &&
//       user.permissions.oneCommerce.preferredCustomer &&
//       user.permissions.oneCommerce.preferredCustomer.access;
// const MINIMUM_CART_QUANTITY_FOR_PC_AUTO_ENROLL = 6;

function getOptimisticResponse({ cart, product, qtyChange }) {
  const isProductPcEnabled = !!(
    product.promoCodeDiscount &&
    product.promoCodeDiscount.case4Cause &&
    product.promoCodeDiscount.case4Cause.case6PriceUSD
  );

  return {
    cartAdd: {
      cart: {
        id: cart.id,
        totalNumberOfItems: cart.totalNumberOfItems + qtyChange,
        viaQualifiedCartTotal: isProductPcEnabled
          ? cart.viaQualifiedCartTotal + qtyChange
          : cart.viaQualifiedCartTotal,
        autoShipEnabled: cart.autoShipEnabled,
        autoShipIntervalUnit: cart.autoShipIntervalUnit,
        autoShipIntervalCount: cart.autoShipIntervalCount,
        c4cMode: cart.c4cMode,
        enrollingAsPc: cart.enrollingAsPc,
        totalSavings: cart.totalSavings,
        promoCodeSavings: cart.promoCodeSavings,
        pcPotentialSavings: cart.pcPotentialSavings,
        subTotalPriceOfItems: cart.subTotalPriceOfItems,
        subTotalPriceOfItemsWithPromoCodes:
          cart.subTotalPriceOfItemsWithPromoCodes,
        eventId: cart.eventId,
        promoCodeDiscountedItems: cart.promoCodeDiscountedItems,
        entries: cart.entries,
        entriesV2: cart.entriesV2,
        promoCodes: cart.promoCodes,
        event: cart.event,
      },
    },
  };
}

function commit(
  environment: Object,
  router: Object,
  user: Object,
  product: Object,
  quantity: number,
  qtyChange: number,
  isCheckout: boolean,
  onCompleted: () => void,
  onError: () => void,
) {
  const { cart } = user;
  const isInCart = quantity - qtyChange > 0;
  const defaultParams = {
    isCheckout,
    product,
    qtyChange,
    user,
    router,
    environment,
  };

  return commitMutation(environment, {
    mutation,
    variables: { input: getVariables(cart, product, quantity) },
    // optimisticResponse: getOptimisticResponse({ cart, product, qtyChange }),

    updater(store) {
      // if (store && product && cart && user) {
      // sharedUpdater(store, product, cart);
      const payload = store.getRootField('cartAdd');
      if (payload) {
        const newEdge = payload.getLinkedRecord('cartEntryEdge');
        const cartProxy = store.get(cart.id);
        const entries = ConnectionHandler.getConnection(
          cartProxy,
          'Cart_entries',
        );
        const edges = entries.getLinkedRecords('edges');
        if (
          !isInCart &&
          cart.entries &&
          edges.length === cart.entries.edges.length
        ) {
          if (entries) {
            ConnectionHandler.insertEdgeBefore(entries, newEdge);
          }
        }
      }
      // }
    },
    // optimisticUpdater(store) {
    //   const payload = store.get(cart.id);
    //   if (payload) {
    //     const entries = ConnectionHandler.getConnection(
    //       payload,
    //       'Cart_entries',
    //     );
    //     const edges = entries.getLinkedRecords('edges');
    //     let edgeExists = false;
    //     if (edges) {
    //       edges.forEach(edge => {
    //         const node = edge.getLinkedRecord('node');
    //         if (node) {
    //           const productNode = node.getLinkedRecord('product');
    //           if (productNode) {
    //             const productId = productNode.getValue('productId');
    //             if (productId === product.productId) {
    //               node.setValue(quantity, 'quantity');
    //               edgeExists = true;
    //             }
    //           }
    //         }
    //       });
    //     }
    //     if (!edgeExists) {
    //       const id = `client:newProductNode:${product.productId}`;
    //       const node = store.create(id, 'node');
    //       // create product
    //       const productDataId = `client:newProductDataNode:${product.productId}`;
    //       const productData = store.create(productDataId, 'product');
    //       Object.keys(product).forEach(productKey => {
    //         productData.setValue(productData[productKey], productKey);
    //       });
    //       node.setLinkedRecord(productData, 'product');
    //       node.setValue(quantity, 'quantity');
    //       const newEdge = store.create(
    //         `client:newEdge:${product.productId}`,
    //         'cartEntryEdge',
    //       );
    //       newEdge.setLinkedRecord(node, 'edges');
    //       sharedUpdater(store, cart, newEdge);
    //     }
    //   }
    //   // }
    // },
    onCompleted: (response, error) =>
      getOnCompleted(onCompleted, { ...defaultParams, response, error }),
    onError:
      onError ||
      function consoleError(err) {
        console.error(err);
      },
  });
}

/**
 * CartAddMutation is a singleton. This is to ensure that all
 * calls to CartAddMutation is debounced from all pages.
 * It doesn't matter if the call is from CartRightNav or
 * Product Details Page
 */

class CartAddMutation {
  constructor() {
    if (!CartAddMutation.instance) {
      this._queue = [];
      this._isResolving = false;
      CartAddMutation.instance = this;
    }
    return CartAddMutation.instance;
  }

  get isResolving() {
    return this._isResolving;
  }

  set isResolving(isResolving) {
    this._isResolving = isResolving;
  }

  get queue() {
    return this._queue;
  }

  set queue(queuedCommit) {
    this._queue = [queuedCommit];
  }

  onSuccess = () => {
    this.isResolving = false;
    if (this.queue.length) {
      const queuedCommit = this.queue[0];
      this.queue = [];
      const {
        environment,
        router,
        user,
        product,
        quantity,
        qtyChange,
        isCheckout,
        onCompleted,
        onError,
      } = queuedCommit;
      return this.commit(
        environment,
        router,
        user,
        product,
        quantity,
        qtyChange,
        isCheckout,
        onCompleted,
        onError,
      );
    }
  };

  commit = (
    environment,
    router,
    user,
    product,
    quantity,
    qtyChange,
    isCheckout,
    onCompleted,
    onError,
  ) => {
    if (!user) {
      return;
    }

    const onCompletedWithQueue = onCompleted;

    return commit(
      environment,
      router,
      user,
      product,
      quantity,
      qtyChange,
      isCheckout,
      onCompletedWithQueue,
      onError,
    );
  };
}

const instance = new CartAddMutation();

export default instance;
